// /app/frontend/src/components/Inbox.js
import React, { useEffect, useState, useRef } from "react";
import apiClient from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useTypingStore } from "../context/TypingContext";
import { API } from "../config";
import { getRealtimeClient } from "../services/realtime";
import { subscribeToMatchMessages } from "../lib/realtime";
import { useLocation, useNavigate } from "react-router-dom";
import { Heart, ArrowLeft } from "lucide-react";
import GiftPickerModal from "./GiftPickerModal";
import { resolveProfilePhotoUrl } from "../utils/profileMedia";
import { formatMessagePreview, resolveMessageAuthor } from "../utils/messageAuthor";
import InlineNotice from "./common/InlineNotice";

function formatTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleString();
}

export default function Inbox() {
  const { token, user } = useAuth();
  const { typingMap, setTypingForMatch } = useTypingStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [loadingInbox, setLoadingInbox] = useState(true);
  const [inbox, setInbox] = useState([]);
  const [selectedConvId, setSelectedConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendText, setSendText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  
  // Real-time state
  const [realtimeClient, setRealtimeClient] = useState(null);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const ablyChannelRef = useRef(null);
  
  // 🎯 Patch 7 Part 4: Auto-select conversation from URL
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  
  // Gift modal state
  const [showGiftModal, setShowGiftModal] = useState(false);
  
  // Track typing timeouts for auto-clear
  const typingTimeoutsRef = useRef({});

  // 🔧 FIX: Mark messages as seen when viewing inbox
  // Guard against double-clear in StrictMode
  const didClearRef = useRef(false);
  
  useEffect(() => {
    const markMessagesSeen = async () => {
      if (!token || didClearRef.current) return;
      didClearRef.current = true;
      
      try {
        const response = await apiClient.post(
          `/notifications/clear`,
          { type: 'messages' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Use returned summary immediately (faster than re-fetch)
        if (response.data?.summary) {
          window.updateBadgeCounts?.(response.data.summary);
          window.updateDashboardBadges?.(response.data.summary);
        } else {
          window.refreshNotificationSummary?.();
        }
      } catch (err) {
        console.error('[Inbox] Failed to mark messages as seen:', err);
      }
    };
    
    markMessagesSeen();
  }, [token]);

  // Load inbox list
  const loadInbox = async () => {
    if (!token) return;
    setLoadingInbox(true);
    setError(null);
    try {
      const res = await apiClient.get(`/chat/inbox`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInbox(res.data || []);
      
      // Clear messages notification badge when inbox is viewed
      try {
        await apiClient.post(`/notifications/clear`, 
          { type: 'messages' },
          { headers: { Authorization: `Bearer ${token}` }}
        );
      } catch (err) {
        console.error('[Inbox] Failed to clear notification', err);
      }
    } catch (err) {
      console.error("Failed to load inbox", err);
      setError("Couldn't load your messages. Check your connection and try again.");
    } finally {
      setLoadingInbox(false);
    }
  };

  // Load messages for a conversation
  const loadMessages = async (conversationId) => {
    if (!token || !conversationId) return;
    setLoadingMessages(true);
    setError(null);
    try {
      const res = await apiClient.get(`/chat/conversation/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(res.data?.messages || []);

      // Mark as read
      await apiClient.post(
        `/chat/mark-read`,
        { conversation_id: conversationId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh inbox to update unread counts
      loadInbox();
    } catch (err) {
      console.error("Failed to load conversation", err);
      setError("Couldn't open this conversation. Try refreshing the page.");
    } finally {
      setLoadingMessages(false);
    }
  };

  // Initialize Ably realtime client once
  useEffect(() => {
    if (!token) return;

    const initRealtime = async () => {
      try {
        const client = await getRealtimeClient();
        setRealtimeClient(client);
        console.log("[inbox] Realtime client initialized");
      } catch (err) {
        console.error("[inbox] Failed to init realtime client", err);
      }
    };

    initRealtime();
  }, [token]);

  // Inbox Live Sync - Subscribe to user's inbox channel for real-time updates
  useEffect(() => {
    if (!realtimeClient || !user) return;

    const userInboxChannel = `inbox:user:${user.id || user._id}`;
    
    try {
      const channel = realtimeClient.channels.get(userInboxChannel);
      
      // Add safe error handlers
      const safeErrorHandler = (err) => {
        console.error('[Inbox Live Sync] Channel error:', err);
      };
      const safeDetachedHandler = (stateChange) => {
        console.warn('[Inbox Live Sync] Channel detached:', stateChange?.reason || '');
      };
      
      channel.off('error', safeErrorHandler);
      channel.off('detached', safeDetachedHandler);
      channel.on('error', safeErrorHandler);
      channel.on('detached', safeDetachedHandler);

      const handleInboxUpdate = (msg) => {
        const data = msg.data || {};
        
        if (data.type === 'inbox_update') {
          console.log('[Inbox Live Sync] Received update:', data);
          
          const { conversation_id, match_id, last_message, unread_count } = data;
          const targetId = conversation_id || match_id;
          
          if (!targetId) return;
          
          // Update inbox list
          setInbox((prevInbox) => {
            const copy = [...prevInbox];
            const idx = copy.findIndex(
              (c) => c.id === targetId || c.conversation_id === targetId || c.match_id === targetId
            );
            
            if (idx === -1) {
              // New conversation - refresh inbox
              loadInbox();
              return prevInbox;
            }
            
            // Update existing conversation with author info
            copy[idx] = {
              ...copy[idx],
              last_message: last_message ? {
                sender_id: last_message.from_user_id,
                author_user_id: last_message.author_user_id || last_message.from_user_id,
                author_name: last_message.author_name,
                text: last_message.text,
                sent_at: last_message.sent_at,
              } : copy[idx].last_message,
              unread_count: typeof unread_count === 'number' ? unread_count : copy[idx].unread_count,
              updated_at: last_message?.sent_at || copy[idx].updated_at,
            };
            
            // Sort by most recent first
            copy.sort((a, b) => {
              const aTime = new Date(a.updated_at || a.created_at || 0);
              const bTime = new Date(b.updated_at || b.created_at || 0);
              return bTime - aTime;
            });
            
            return copy;
          });
        }
      };

      channel.subscribe('inbox_update', handleInboxUpdate);
      console.log(`[Inbox Live Sync] Subscribed to ${userInboxChannel}`);
      
      return () => {
        try {
          channel.unsubscribe('inbox_update', handleInboxUpdate);
          console.log(`[Inbox Live Sync] Unsubscribed from ${userInboxChannel}`);
        } catch (err) {
          console.error('[Inbox Live Sync] Error unsubscribing:', err);
        }
      };
    } catch (err) {
      console.error('[Inbox Live Sync] Failed to subscribe:', err);
    }
  }, [realtimeClient, user, loadInbox]);

  // Global typing indicator listener for all inbox conversations
  useEffect(() => {
    if (!realtimeClient || !inbox.length || !user) return;

    const subscriptions = [];

    // Subscribe to typing events for each conversation
    inbox.forEach((conv) => {
      const matchId = conv.match_id || conv.id;
      const channelName = `chat:match:${matchId}`;
      
      try {
        const channel = realtimeClient.channels.get(channelName);
        
        // Add safe error handlers
        const safeErrorHandler = (err) => {
          console.error(`[Inbox Typing] Channel error for ${matchId}:`, err);
        };
        const safeDetachedHandler = (stateChange) => {
          console.warn(`[Inbox Typing] Channel detached for ${matchId}:`, stateChange?.reason || '');
        };
        
        channel.off('error', safeErrorHandler);
        channel.off('detached', safeDetachedHandler);
        channel.on('error', safeErrorHandler);
        channel.on('detached', safeDetachedHandler);

        const handler = (msg) => {
          const data = msg.data || {};
          
          // Ignore our own typing events
          if (!data || data.fromUserId === (user.id || user._id)) return;
          
          if (data.type === "typing") {
            if (data.isTyping) {
              // Set typing state
              setTypingForMatch(matchId, true);
              
              // Clear any existing timeout for this match
              if (typingTimeoutsRef.current[matchId]) {
                clearTimeout(typingTimeoutsRef.current[matchId]);
              }
              
              // Auto-clear after 7 seconds (fallback in case stop event is missed)
              typingTimeoutsRef.current[matchId] = setTimeout(() => {
                setTypingForMatch(matchId, false);
                delete typingTimeoutsRef.current[matchId];
              }, 7000);
            } else {
              // Clear typing state
              setTypingForMatch(matchId, false);
              if (typingTimeoutsRef.current[matchId]) {
                clearTimeout(typingTimeoutsRef.current[matchId]);
                delete typingTimeoutsRef.current[matchId];
              }
            }
          }
        };

        channel.subscribe("typing", handler);
        subscriptions.push(() => {
          channel.unsubscribe("typing", handler);
        });
      } catch (err) {
        console.error(`[Inbox Typing] Failed to subscribe to ${matchId}:`, err);
      }
    });

    // Cleanup: unsubscribe from all typing events
    return () => {
      subscriptions.forEach(unsub => unsub());
      // Clear all timeouts
      Object.values(typingTimeoutsRef.current).forEach(timeout => clearTimeout(timeout));
      typingTimeoutsRef.current = {};
    };
  }, [realtimeClient, inbox, user, setTypingForMatch]);

  // Subscribe to the current conversation's realtime channel - UNIFIED
  useEffect(() => {
    if (!selectedConvId) {
      console.log("[inbox] Skipping subscription - selectedConvId missing");
      return;
    }

    // Find the current conversation to get match_id if available
    const currentConv = inbox.find(c => c.id === selectedConvId);
    const matchId = currentConv?.match_id || selectedConvId;
    
    console.log("[inbox] Subscribing with matchId:", matchId);

    const unsubscribe = subscribeToMatchMessages(matchId, (data) => {
      console.log('[inbox] message from Ably', data);

      // Basic shape check
      if (!data || !data.id || !data.text) {
        console.log("[inbox] Invalid message payload", data);
        return;
      }

      // Ignore messages we just sent ourselves
      if (user && data.senderId === (user.id || user._id)) {
        console.log("[inbox] Ignoring own message");
        return;
      }

      setMessages((prev) => {
        // Avoid duplicates
        if (data.id && prev.some((m) => m.id === data.id)) {
          console.log("[inbox] Message already exists, skipping");
          return prev;
        }

        const newMsg = {
          id: data.id || `${Date.now()}`,
          sender_id: data.senderId,
          text: data.text,
          content: data.text,
          created_at: data.createdAt,
          timestamp: new Date(data.createdAt).getTime() / 1000,
        };
        
        console.log("[inbox] Adding message to list");
        return [...prev, newMsg];
      });

      // Also update inbox list preview
      setInbox((prev) =>
        prev.map((c) =>
          c.id === selectedConvId
            ? {
                ...c,
                last_message: { text: data.text, created_at: data.createdAt },
                updated_at: data.createdAt,
                unread_count: (c.unread_count || 0) + 1,
              }
            : c
        )
      );
    });

    return unsubscribe;
  }, [selectedConvId, inbox, user]);

  // Fetch global unread count
  useEffect(() => {
    if (!token) return;

    const fetchUnread = async () => {
      try {
        const res = await apiClient.get(`/chat/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUnreadTotal(res.data?.unread_count || 0);
      } catch (err) {
        console.error("[inbox] Failed to fetch unread count", err);
      }
    };

    fetchUnread();

    // Optional: poll every 30s as a backup to realtime
    const id = setInterval(fetchUnread, 30000);
    return () => clearInterval(id);
  }, [token]);

  // Initial load
  useEffect(() => {
    loadInbox();
  }, [token]);

  // 🎯 Patch 7 Part 4: Auto-select conversation from URL query
  useEffect(() => {
    if (!hasAutoSelected && inbox.length > 0) {
      const searchParams = new URLSearchParams(location.search);
      const initialConvId = searchParams.get("conversationId");
      
      if (initialConvId) {
        const conv = inbox.find(c => c.id === initialConvId);
        if (conv) {
          console.log("[inbox] Auto-selecting conversation from URL:", initialConvId);
          handleSelectConversation(initialConvId);
          setHasAutoSelected(true);
        }
      }
    }
  }, [inbox, location.search, hasAutoSelected]);

  const handleSelectConversation = (conv) => {
    if (!conv) return;

    // Navigate to full Chat page if match_id available
    if (conv.match_id) {
      navigate(`/chat/${conv.match_id}`);
      return;
    }

    // Fallback: legacy inline view
    setSelectedConvId(conv.id);
    loadMessages(conv.id);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!sendText.trim() || !selectedConvId || sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await apiClient.post(
        `/chat/send`,
        {
          conversation_id: selectedConvId,
          text: sendText.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const newMsg = res.data?.message;
      if (newMsg) {
        setMessages((prev) => [...prev, newMsg]);
        
        // Update inbox list preview
        setInbox((prev) =>
          prev.map((c) =>
            c.id === selectedConvId
              ? { ...c, last_message: newMsg, updated_at: newMsg.created_at }
              : c
          )
        );
      }
      setSendText("");

      // 🔴 NEW: publish to Ably so other devices/user get the message
      try {
        if (ablyChannelRef.current && newMsg) {
          ablyChannelRef.current.publish("message:new", {
            message: newMsg,
          });
          console.log("[inbox] Published message to Ably");
        }
      } catch (err) {
        console.error("[inbox] Failed to publish realtime message", err);
      }
    } catch (err) {
      console.error("Failed to send message", err);
      setError("Message didn't send. Tap to retry.");
    } finally {
      setSending(false);
    }
  };

  const currentConv = inbox.find((c) => c.id === selectedConvId);

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      <div className="px-4 py-4 border-b border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center text-xs px-3 py-1 rounded-full bg-gray-900 hover:bg-gray-800 text-gray-100 border border-gray-700 transition"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            <span>Back to Dashboard</span>
          </button>
          <h1 className="text-xl font-bold">
            Messages {unreadTotal > 0 && <span className="ml-2 text-xs px-2 py-1 rounded-full bg-pink-600">{unreadTotal}</span>}
          </h1>
        </div>

        <button
          onClick={loadInbox}
          className="text-xs px-3 py-1 rounded-full bg-gray-800 hover:bg-gray-700"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="px-4 py-2">
          <InlineNotice
            variant="error"
            testId="inbox-error-notice"
            actionLabel="Dismiss"
            onAction={() => setError(null)}
          >
            {error}
          </InlineNotice>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* Left: Inbox list - Hidden on mobile when conversation selected */}
        <div className={`w-full md:w-1/3 md:min-w-[260px] border-r border-gray-800 flex flex-col ${selectedConvId ? 'hidden md:flex' : 'flex'}`}>
          <div className="px-4 py-2 text-xs text-gray-400 border-b border-gray-800">
            Conversations
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingInbox && (
              <div className="p-4 text-sm text-gray-400">Loading inbox…</div>
            )}
            {!loadingInbox && inbox.length === 0 && (
              <div className="p-4 text-sm text-gray-400">
                No conversations yet. Once you connect with someone, your chats will appear here.
              </div>
            )}
            {!loadingInbox &&
              inbox.map((conv) => {
                // Resolve profile photo from conversation data
                const otherUserProfile = conv.other_user_profile || conv.profile;
                const photoUrl = resolveProfilePhotoUrl(otherUserProfile) || 
                  conv.other_user_photo || 
                  conv.other_user_avatar;
                
                // PATCH: Prevent showing literal "None" and "N" avatar for conversations
                const sanitizeName = (v) => {
                  if (!v) return "";
                  const s = String(v).trim();
                  if (!s) return "";
                  if (["none", "null", "undefined"].includes(s.toLowerCase())) return "";
                  return s;
                };
                const displayName = sanitizeName(conv.other_user_name) || "Match";
                const initial = sanitizeName(conv.other_user_name)
                  ? sanitizeName(conv.other_user_name).charAt(0).toUpperCase()
                  : "💬";
                
                return (
                <button
                  key={conv.id}
                  onClick={() => handleSelectConversation(conv)}
                  className={`w-full flex items-center px-4 py-3 text-left hover:bg-gray-800 border-b border-gray-900 ${
                    selectedConvId === conv.id ? "bg-gray-800" : ""
                  }`}
                >
                  {/* Profile photo or fallback initial */}
                  {photoUrl ? (
                    <img 
                      src={photoUrl} 
                      alt={displayName}
                      className="w-10 h-10 rounded-full object-cover mr-3 border border-gray-700"
                      onError={(e) => {
                        // Hide broken image, show fallback
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className={`w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 items-center justify-center text-sm font-bold mr-3 ${
                      photoUrl ? 'hidden' : 'flex'
                    }`}
                  >
                    {initial}
                  </div>
                  {/* FIX #2: Inbox preview truncation - proper flex container with min-width:0 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-sm font-semibold truncate">
                          {displayName}
                        </span>
                        {conv.video_unlocked && (
                          <span 
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-300 border border-emerald-500/40 flex-shrink-0"
                            title="Video chat unlocked"
                          >
                            <svg
                              className="h-2.5 w-2.5"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14" />
                              <rect x="3" y="7" width="12" height="10" rx="2" ry="2" />
                            </svg>
                          </span>
                        )}
                      </div>
                      {conv.unread_count > 0 && (
                        <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-pink-600 flex-shrink-0">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    {/* FIX #2: Preview line with proper text overflow handling */}
                    <div className="text-xs overflow-hidden text-ellipsis whitespace-nowrap">
                      {(() => {
                        const matchId = conv.match_id || conv.id;
                        const isTyping = typingMap[matchId];
                        
                        if (isTyping) {
                          return (
                            <span className="text-emerald-400 italic">
                              Typing…
                            </span>
                          );
                        }
                        
                        // Use author-aware message preview
                        if (conv.last_message) {
                          const preview = formatMessagePreview(conv.last_message, {
                            currentUserId: user?.id || user?._id,
                            maxLength: 50, // Increased for better preview
                          });
                          return (
                            <span className="text-gray-400" data-testid="inbox-message-preview">
                              {preview}
                            </span>
                          );
                        }
                        
                        return (
                          <span className="text-gray-400">
                            Start the conversation ✨
                          </span>
                        );
                      })()}
                    </div>
                    <div className="text-[10px] text-gray-500">
                      {formatTime(conv.updated_at)}
                    </div>
                  </div>
                </button>
              );})}
          </div>
        </div>

        {/* Right: Conversation view - Hidden on mobile when no conversation selected */}
        <div className={`flex-1 flex-col ${selectedConvId ? 'flex' : 'hidden md:flex'}`}>
          {!selectedConvId ? (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
              Select a conversation on the left to start chatting.
            </div>
          ) : (
            <>
              {/* Header with back button, name, and heart */}
              <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {/* Back button - mobile shows back to list, desktop shows back to dashboard */}
                  <button
                    onClick={() => {
                      // On mobile, go back to conversation list
                      // On desktop, go back to dashboard
                      if (window.innerWidth < 768) {
                        setSelectedConvId(null);
                      } else {
                        navigate('/dashboard');
                      }
                    }}
                    className="text-gray-400 hover:text-white flex items-center space-x-1"
                    title="Back"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    <span className="hidden md:inline text-sm">Back to Dashboard</span>
                  </button>
                </div>
                
                <div className="flex items-center space-x-3">
                  {/* User name and status */}
                  <div className="text-center">
                    <div className="text-sm font-semibold text-white">
                      {(() => {
                        const n = currentConv?.other_user_name;
                        if (!n) return "Conversation";
                        const s = String(n).trim();
                        if (["none", "null", "undefined"].includes(s.toLowerCase())) return "Match";
                        return s || "Conversation";
                      })()}
                    </div>
                    <div className="text-xs text-gray-400">
                      {currentConv?.unread_count
                        ? `${currentConv.unread_count} unread`
                        : "All caught up"}
                    </div>
                  </div>
                  
                  {/* Heart button */}
                  {currentConv?.match_id && (
                    <button
                      onClick={() => {
                        // TODO: Implement favorite/heart functionality
                        console.log('Heart clicked for match:', currentConv.match_id);
                      }}
                      className="p-2 rounded-full hover:bg-gray-700 text-pink-500 hover:text-pink-400 transition-colors"
                      title="Favorite"
                    >
                      <Heart className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Legacy mode banner - shown when no match_id (can't use voice/gifts) */}
              {currentConv && !currentConv.match_id && (
                <div className="mx-4 mt-2 rounded-xl bg-yellow-500/10 border border-yellow-500/20 px-3 py-2 flex items-center justify-between">
                  <span className="text-xs text-yellow-200">
                    Basic mode — voice notes and gifts available after matching.
                  </span>
                  <button
                    onClick={() => navigate('/discover')}
                    className="text-xs text-yellow-400 hover:text-yellow-300 underline ml-2"
                  >
                    Discover
                  </button>
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
                {loadingMessages && (
                  <div className="text-sm text-gray-400">Loading messages…</div>
                )}
                {!loadingMessages && messages.length === 0 && (
                  <div className="text-sm text-gray-400">
                    Send the first message to start the conversation.
                  </div>
                )}
                {!loadingMessages &&
                  messages.map((msg) => {
                    const isMe = msg.sender_id === user?.id || msg.sender_id === user?._id;
                    // Use canonical author resolution
                    const { displayName, isCoupleMessage } = resolveMessageAuthor(msg, {
                      currentUserId: user?.id || user?._id,
                    });
                    
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${
                          isMe ? "justify-end" : "justify-start"
                        }`}
                        data-testid={`inbox-message-${msg.id}`}
                      >
                        <div
                          className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                            isMe
                              ? "bg-pink-600 text-white rounded-br-none"
                              : "bg-gray-800 text-gray-100 rounded-bl-none"
                          }`}
                        >
                          {/* Show author for received couple messages */}
                          {!isMe && isCoupleMessage && (
                            <div className="text-[10px] text-purple-400 font-medium mb-1" data-testid="message-author-label">
                              {displayName} <span className="text-gray-400">(for the couple)</span>
                            </div>
                          )}
                          {/* Voice message or text content */}
                          {msg.type === "audio" && msg.audio_url ? (
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                  isMe ? "bg-white/20 hover:bg-white/30 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const audio = new Audio(`${window.location.origin}${msg.audio_url}`);
                                  audio.play().catch((err) => console.error("Failed to play audio:", err));
                                }}
                              >
                                ▶
                              </button>
                              <span className={`text-xs ${isMe ? "text-white/80" : "text-gray-300"}`}>
                                Voice message · {Math.round((msg.duration_ms || 0) / 1000)}s
                              </span>
                            </div>
                          ) : (
                            <div>{msg.text || msg.content || ""}</div>
                          )}
                          <div className="text-[10px] text-gray-300 mt-1 text-right">
                            {formatTime(msg.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>

              {/* Input */}
              <form
                onSubmit={handleSend}
                className="border-t border-gray-800 px-3 py-2 flex items-center space-x-2"
              >
                <input
                  type="text"
                  value={sendText}
                  onChange={(e) => setSendText(e.target.value)}
                  placeholder="Type a message…"
                  className="flex-1 bg-gray-800 rounded-full px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-pink-500"
                />
                
                {/* Gift button */}
                <button
                  type="button"
                  onClick={() => setShowGiftModal(true)}
                  className="inline-flex items-center justify-center rounded-full bg-pink-600 hover:bg-pink-500 text-white w-9 h-9 text-lg transition-colors"
                  title="Send a virtual gift"
                >
                  💜
                </button>
                
                <button
                  type="submit"
                  disabled={sending || !sendText.trim()}
                  className="px-4 py-2 rounded-full bg-pink-600 text-sm font-semibold disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Send"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
      
      {/* Gift Picker Modal */}
      {currentConv && (
        <GiftPickerModal
          open={showGiftModal}
          onClose={() => setShowGiftModal(false)}
          targetUserId={currentConv.other_user_id}
          matchId={currentConv.match_id || selectedConvId}
          recipientName={(() => {
            const n = currentConv.other_user_name;
            if (!n) return "them";
            const s = String(n).trim();
            if (["none", "null", "undefined"].includes(s.toLowerCase())) return "them";
            return s || "them";
          })()}
        />
      )}
    </div>
  );
}
